import React from 'react';
import { getDrugIconPaths } from '../../data/keystoneCards';
import { DRUG_ICONS } from '../../data/drugIcons';

// Fallback for an off-tree slug — the canonical "completed" green ProgressMap
// uses (mirrors SkillTreeOwnedCluster.GREEN).
const GREEN = '#22c55e';

/**
 * SkillTreeNodeGlyph — the SINGLE source of truth for a skill-tree node atom's
 * visual: the stroked circle + its glyph (keystone path / per-slug drug stroke /
 * recraft DRUG_ICONS fill / equipped check), drawn EXACTLY as the hotbar atom and
 * the ProgressMap node draw it (same fill `${accent}2e`, accent stroke ring, and
 * the `translate(-r*0.65,-r*0.65) scale(r*1.3/24)` glyph incantation).
 *
 * Returns the inner SVG content centered at the origin, so a consumer places it
 * inside an <svg> within `<g transform="translate(cx,cy)">`.
 *
 * Used by BOTH SkillTreeOwnedCluster (the hotbar) and SkillTreeNodeToast (the
 * tooltip header) so the tooltip is a TRUE PORT of the node rendering — the same
 * component, never a lookalike recreation. (Edward 2026-06-26)
 */
export default function SkillTreeNodeGlyph({
  isKeystone = false,
  slug = null,
  iconPath = null,   // keystone glyph path (getKeystoneIconPath) — keystones only
  accent,
  radius = 18,
  equipped = true,
}) {
  const stroke = accent || GREEN;
  const fill = isKeystone
    ? `${stroke}2e`  // ~18% alpha hex suffix
    : (equipped ? `${stroke}2e` : 'rgba(255, 255, 255, 0.06)');

  // Glyph resolution EXACTLY as the tree node does: per-slug STROKE glyph
  // (getDrugIconPaths) wins; recraft DRUG_ICONS fill is the fallback; the check
  // is the last resort so equipped state still reads.
  const drugStrokePaths = !isKeystone ? getDrugIconPaths(slug) : null;
  const hasDrugStroke = Array.isArray(drugStrokePaths) && drugStrokePaths.length > 0;
  const drugGlyph = (!isKeystone && !hasDrugStroke)
    ? DRUG_ICONS[String(slug || '').replace(/[\s-]+/g, '_')]
    : null;
  const showCheck = !isKeystone && equipped && !hasDrugStroke && !drugGlyph;

  const glyphTransform =
    `translate(${-radius * 0.65}, ${-radius * 0.65}) scale(${(radius * 1.3) / 24})`;

  return (
    <>
      <circle r={radius} fill={fill} stroke={stroke} strokeWidth={isKeystone ? 1.8 : 1.5} />
      {/* Canonical keystone glyph (mirrors ProgressMap keystone icon block). */}
      {isKeystone && iconPath && (
        <g transform={glyphTransform} style={{ pointerEvents: 'none' }}>
          <path
            d={iconPath}
            fill="none"
            stroke={stroke}
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      )}
      {/* Canonical per-slug STROKE glyph — the same `d` strings MapNode draws. */}
      {hasDrugStroke && (
        <g transform={glyphTransform} style={{ pointerEvents: 'none' }}>
          {drugStrokePaths.map((d, i) => (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={stroke}
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </g>
      )}
      {drugGlyph && (
        <svg
          x={-radius * 0.7}
          y={-radius * 0.7}
          width={radius * 1.4}
          height={radius * 1.4}
          viewBox={drugGlyph.viewBox}
          style={{ pointerEvents: 'none' }}
          aria-hidden="true"
        >
          {drugGlyph.paths.map((d, i) => (
            <path key={i} d={d} fill={stroke} />
          ))}
        </svg>
      )}
      {showCheck && (
        <path
          d={`M${-radius * 0.3},0 L${-radius * 0.1},${radius * 0.25} L${radius * 0.3},${-radius * 0.2}`}
          fill="none"
          stroke="#fff"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </>
  );
}
